import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import {
  crc, METODOS_PAGO, TIPOS_COMPROBANTE, TIPOS_ID,
  cobraIvaAparte, TIPOS_ELECTRONICOS, LEYENDA_IVA_INCLUIDO,
} from '../utils/format.js';
import { Modal } from '../components/UI.jsx';
import { Icon } from '../components/Icons.jsx';
import ComprobanteView from '../components/ComprobanteView.jsx';
import useContribuyenteLookup from '../hooks/useContribuyenteLookup.js';

const CLIENTE_VACIO = {
  nombre: '',
  tipo_identificacion: '01',
  identificacion: '',
  email: '',
  telefono: '',
  direccion: '',
};

export default function POS() {
  const toast = useToast();
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [catActiva, setCatActiva] = useState(null);
  const [carrito, setCarrito] = useState([]);

  const [clienteId, setClienteId] = useState('');
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [tipoComp, setTipoComp] = useState('ticket');
  // 01 contado | 02 credito → cuenta por cobrar
  const [condicionVenta, setCondicionVenta] = useState('01');
  const [plazoCredito, setPlazoCredito] = useState('30');
  const [cobrando, setCobrando] = useState(false);
  const [ventaFinal, setVentaFinal] = useState(null);
  const [efectivoRecibido, setEfectivoRecibido] = useState('');
  // Credito: lo que el cliente deja pagado hoy y con que lo paga.
  const [abonoInicial, setAbonoInicial] = useState('');
  const [metodoAbono, setMetodoAbono] = useState('efectivo');
  // Descuento global: modo 'pct' (%) o 'monto' (₡)
  const [descGlobalModo, setDescGlobalModo] = useState('pct');
  const [descGlobalValor, setDescGlobalValor] = useState('');
  const [feActiva, setFeActiva] = useState(false);
  const [cajaAbierta, setCajaAbierta] = useState(undefined);

  // Alta rapida de cliente sin salir del POS
  const [modalCliente, setModalCliente] = useState(false);
  const [formCliente, setFormCliente] = useState(CLIENTE_VACIO);
  const [guardandoCliente, setGuardandoCliente] = useState(false);
  // Si el usuario edita el nombre a mano, no lo pisamos con la consulta
  const [nombreManual, setNombreManual] = useState(false);

  const lookup = useContribuyenteLookup(formCliente.identificacion, {
    enabled: modalCliente,
  });

  // Autocompleta desde Hacienda al tener 9–12 digitos
  useEffect(() => {
    if (!modalCliente || lookup.estado !== 'ok' || !lookup.datos) return;
    const d = lookup.datos;
    setFormCliente((f) => ({
      ...f,
      tipo_identificacion: d.tipo_identificacion || f.tipo_identificacion,
      identificacion: d.identificacion || f.identificacion,
      nombre: nombreManual && f.nombre.trim() ? f.nombre : d.nombre || f.nombre,
      codigo_actividad: d.codigo_actividad || f.codigo_actividad || '',
    }));
  }, [lookup.estado, lookup.datos, modalCliente, nombreManual]);

  const cargar = () => {
    api.get('/productos').then((r) => setProductos(r.data.data));
    api.get('/categorias').then((r) => setCategorias(r.data.data));
    api.get('/clientes').then((r) => setClientes(r.data.data));
    api.get('/empresa').then((r) => setFeActiva(!!r.data.data?.fe_activa));
    api.get('/cajas/actual').then((r) => setCajaAbierta(!!r.data.data)).catch(() => setCajaAbierta(false));
  };
  useEffect(cargar, []);

  // Con la factura electronica apagada solo se pueden emitir tiquetes
  // normales y proformas: los electronicos ni se ofrecen.
  const tiposDisponibles = useMemo(
    () => (feActiva ? TIPOS_COMPROBANTE : TIPOS_COMPROBANTE.filter((t) => !TIPOS_ELECTRONICOS.includes(t.value))),
    [feActiva]
  );

  // Si estaba seleccionado un electronico y se apaga la FE, se vuelve a ticket.
  useEffect(() => {
    if (!feActiva && TIPOS_ELECTRONICOS.includes(tipoComp)) setTipoComp('ticket');
  }, [feActiva, tipoComp]);

  const abrirNuevoCliente = () => {
    setFormCliente(CLIENTE_VACIO);
    setNombreManual(false);
    lookup.limpiar();
    setModalCliente(true);
  };

  const guardarCliente = async () => {
    if (!formCliente.nombre?.trim()) {
      toast.error('El nombre del cliente es requerido');
      return;
    }
    const requiereId =
      tipoComp === 'factura_electronica' || tipoComp === 'factura_exportacion';
    const idLimpio = String(formCliente.identificacion || '').replace(/\D/g, '');
    if (requiereId && !idLimpio) {
      toast.error('Para factura electronica o exportacion la cedula / ID es obligatoria');
      return;
    }
    setGuardandoCliente(true);
    try {
      const { data } = await api.post('/clientes', {
        ...formCliente,
        nombre: formCliente.nombre.trim(),
        identificacion: idLimpio || formCliente.identificacion || '',
        codigo_actividad: formCliente.codigo_actividad || '',
      });
      const nuevo = data.data;
      setClientes((prev) => {
        const sinDup = prev.filter((c) => c.id !== nuevo.id);
        return [...sinDup, nuevo].sort((a, b) =>
          String(a.nombre).localeCompare(String(b.nombre), 'es')
        );
      });
      setClienteId(String(nuevo.id));
      setModalCliente(false);
      setFormCliente(CLIENTE_VACIO);
      setNombreManual(false);
      toast.success(`Cliente "${nuevo.nombre}" listo para la venta`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo crear el cliente');
    } finally {
      setGuardandoCliente(false);
    }
  };

  const filtrados = useMemo(() => productos.filter((p) => {
    const okCat = !catActiva || p.categoria_id === catActiva;
    const okBusq = !busqueda || p.nombre.toLowerCase().includes(busqueda.toLowerCase()) || (p.sku || '').toLowerCase().includes(busqueda.toLowerCase());
    return okCat && okBusq;
  }), [productos, catActiva, busqueda]);

  const agregar = (prod) => {
    if (prod.stock <= 0) { toast.error('Sin existencias de ' + prod.nombre); return; }
    setCarrito((c) => {
      const existe = c.find((i) => i.id === prod.id);
      if (existe) {
        if (existe.cantidad >= prod.stock) { toast.error('No hay mas stock'); return c; }
        return c.map((i) => i.id === prod.id ? { ...i, cantidad: i.cantidad + 1 } : i);
      }
      return [...c, {
        id: prod.id,
        nombre: prod.nombre,
        precio_venta: Number(prod.precio_venta),
        tarifa_iva: Number(prod.tarifa_iva),
        cantidad: 1,
        stock: prod.stock,
        // Descuento de linea: monto en colones y % de referencia en UI
        descuento: 0,
        descuento_pct: '',
      }];
    });
  };

  const cambiarCantidad = (id, delta) => {
    setCarrito((c) => c.map((i) => {
      if (i.id !== id) return i;
      const nueva = i.cantidad + delta;
      if (nueva <= 0) return null;
      if (nueva > i.stock) { toast.error('Stock maximo alcanzado'); return i; }
      // Recalcula descuento si estaba en %
      const bruto = nueva * i.precio_venta;
      let descuento = Number(i.descuento) || 0;
      if (i.descuento_pct !== '' && i.descuento_pct != null) {
        const pct = Math.min(100, Math.max(0, Number(i.descuento_pct) || 0));
        descuento = Math.round((bruto * pct) / 100 * 100) / 100;
      }
      if (descuento > bruto) descuento = bruto;
      return { ...i, cantidad: nueva, descuento };
    }).filter(Boolean));
  };

  const quitar = (id) => setCarrito((c) => c.filter((i) => i.id !== id));

  /** Descuento de linea en ₡ (cap al bruto de la linea). */
  const setDescuentoMonto = (id, valor) => {
    setCarrito((c) => c.map((i) => {
      if (i.id !== id) return i;
      const bruto = i.cantidad * i.precio_venta;
      let mon = Number(valor);
      if (Number.isNaN(mon) || mon < 0) mon = 0;
      if (mon > bruto) mon = bruto;
      return {
        ...i,
        descuento: mon,
        descuento_pct: bruto > 0 ? String(Math.round((mon / bruto) * 10000) / 100) : '',
      };
    }));
  };

  /** Descuento de linea en % → se guarda el monto en ₡. */
  const setDescuentoPct = (id, valor) => {
    setCarrito((c) => c.map((i) => {
      if (i.id !== id) return i;
      const bruto = i.cantidad * i.precio_venta;
      let pct = Number(valor);
      if (Number.isNaN(pct) || pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      const mon = Math.round((bruto * pct) / 100 * 100) / 100;
      return { ...i, descuento: mon, descuento_pct: valor === '' ? '' : String(pct) };
    }));
  };

  // El IVA solo se suma en los comprobantes electronicos. En un tiquete
  // normal el impuesto ya viene dentro del precio del producto.
  const desglosaIva = cobraIvaAparte(tipoComp);

  const totales = useMemo(() => {
    let subtotal = 0;
    let descuentoLineas = 0;
    let impuesto = 0;
    carrito.forEach((i) => {
      const bruto = i.cantidad * i.precio_venta;
      let desc = Number(i.descuento) || 0;
      if (desc > bruto) desc = bruto;
      if (desc < 0) desc = 0;
      const base = bruto - desc;
      subtotal += bruto;
      descuentoLineas += desc;
      if (desglosaIva) impuesto += base * (i.tarifa_iva / 100);
    });
    const netoLineas = subtotal - descuentoLineas;
    let descGlobal = 0;
    const raw = Number(descGlobalValor);
    if (descGlobalValor !== '' && !Number.isNaN(raw) && raw > 0) {
      if (descGlobalModo === 'pct') {
        const pct = Math.min(100, raw);
        descGlobal = Math.round((netoLineas * pct) / 100 * 100) / 100;
      } else {
        descGlobal = Math.min(netoLineas, raw);
      }
    }
    const descuento = descuentoLineas + descGlobal;
    const total = subtotal - descuento + impuesto;
    return {
      subtotal,
      descuentoLineas,
      descuentoGlobal: descGlobal,
      descuento,
      impuesto,
      total: total < 0 ? 0 : total,
    };
  }, [carrito, descGlobalModo, descGlobalValor, desglosaIva]);

  const cambio = efectivoRecibido ? Number(efectivoRecibido) - totales.total : 0;

  const esCredito = condicionVenta === '02';
  const requiereCliente =
    tipoComp === 'factura_electronica' ||
    tipoComp === 'factura_exportacion' ||
    esCredito;

  const cobraSinCaja = tipoComp !== 'proforma' && cajaAbierta !== true;

  const cobrar = async () => {
    if (carrito.length === 0) return;
    if (cobraSinCaja) {
      toast.error('No hay una caja abierta. Abra la caja para facturar.');
      return;
    }
    if (esCredito && tipoComp === 'proforma') {
      toast.error('La proforma no se registra a credito. Cambie a contado o use factura/tiquete.');
      return;
    }
    if (requiereCliente && !clienteId) {
      toast.error(
        esCredito
          ? 'Las ventas a credito requieren un cliente'
          : tipoComp === 'factura_exportacion'
            ? 'La factura de exportacion requiere un cliente'
            : 'La factura electronica requiere un cliente con cedula'
      );
      return;
    }
    // Validar descuentos en cliente antes de enviar
    for (const i of carrito) {
      const bruto = i.cantidad * i.precio_venta;
      if ((Number(i.descuento) || 0) > bruto + 0.001) {
        toast.error(`Descuento invalido en ${i.nombre}`);
        return;
      }
    }
    if (totales.descuentoGlobal > totales.subtotal - totales.descuentoLineas + 0.001) {
      toast.error('El descuento global supera el subtotal');
      return;
    }

    // Contado en efectivo: si se anoto lo que entrego el cliente, tiene que
    // alcanzar. Antes se facturaba igual una venta de 3000 con 1000 recibidos.
    const pagaEfectivo = !esCredito && metodoPago === 'efectivo' && tipoComp !== 'proforma';
    const anotoRecibido = efectivoRecibido !== '';
    if (pagaEfectivo && anotoRecibido && Number(efectivoRecibido) + 0.001 < totales.total) {
      toast.error(`El efectivo recibido no alcanza: faltan ${crc(totales.total - Number(efectivoRecibido))}`);
      return;
    }

    const abonoIni = esCredito ? Math.round((Number(abonoInicial) || 0) * 100) / 100 : 0;
    if (esCredito) {
      if (abonoIni < 0) {
        toast.error('El abono inicial no puede ser negativo');
        return;
      }
      if (abonoIni > 0 && abonoIni + 0.001 >= totales.total) {
        toast.error('Si el cliente paga el total no es credito: cambie la venta a contado');
        return;
      }
    }
    const totalVenta = totales.total;

    setCobrando(true);
    try {
      const { data } = await api.post('/ventas', {
        cliente_id: clienteId || null,
        items: carrito.map((i) => ({
          producto_id: i.id,
          cantidad: i.cantidad,
          descuento: Number(i.descuento) || 0,
        })),
        descuento_global: totales.descuentoGlobal,
        metodo_pago: metodoPago,
        tipo_comprobante: tipoComp,
        condicion_venta: condicionVenta,
        plazo_credito: esCredito ? Number(plazoCredito) || 30 : 0,
        // El servidor vuelve a revisar que alcance: la pantalla no es la
        // unica defensa.
        monto_recibido: pagaEfectivo && anotoRecibido ? Number(efectivoRecibido) : null,
        abono_inicial: abonoIni,
        metodo_abono: metodoAbono,
      });
      const venta = data.data;
      const full = await api.get(`/ventas/${venta.id}`);
      setVentaFinal(full.data.data);
      if (esCredito) {
        toast.success(abonoIni > 0
          ? `Credito registrado · abono de ${crc(abonoIni)} · quedan ${crc(totalVenta - abonoIni)} por cobrar`
          : 'Venta a credito registrada · queda en Cuentas por cobrar (pendiente)');
      } else if (tipoComp === 'proforma') {
        toast.success('Proforma emitida (no se envia a Hacienda ni descuenta stock)');
      } else if (venta.fe?.estado === 'enviado' || venta.fe?.estado === 'aceptado') {
        toast.success(`Comprobante enviado a Hacienda (${venta.fe.estado})`);
      } else if (venta.fe?.estado === 'generado') {
        toast.success(`Comprobante electronico generado (${venta.fe.ambiente || ''})`);
      } else if (venta.fe?.estado === 'inactiva') {
        toast.info('Venta registrada. La factura electronica esta desactivada.');
      } else if (venta.fe?.estado === 'error') {
        toast.error(venta.fe.mensaje || 'Venta OK, pero fallo el envio a Hacienda');
      } else {
        toast.success('Venta completada');
      }
      // El envio del comprobante no bloquea la venta: se avisa aparte
      if (venta.correo?.enviado) {
        toast.success(
          venta.correo.simulado
            ? `Correo simulado a ${venta.correo.to} (falta configurar SMTP)`
            : `Comprobante enviado a ${venta.correo.to}`
        );
      } else if (venta.correo && venta.correo.motivo !== 'No hay correo de destino') {
        toast.error(`Venta OK, pero el correo fallo: ${venta.correo.motivo}`);
      }
      setCarrito([]);
      setClienteId('');
      setEfectivoRecibido(''); setAbonoInicial(''); setMetodoAbono('efectivo');
      setDescGlobalValor('');
      setDescGlobalModo('pct');
      setCondicionVenta('01');
      setPlazoCredito('30');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al procesar la venta');
    } finally {
      setCobrando(false);
    }
  };

  return (
    <div className="pos-layout">
      {cajaAbierta === false && (
        <div
          className="card"
          style={{
            gridColumn: '1 / -1',
            padding: 14,
            marginBottom: 0,
            border: '1px solid var(--danger)',
            background: 'var(--danger-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <strong>Caja cerrada.</strong>
            <span className="muted" style={{ marginLeft: 6 }}>
              No se puede facturar ni cobrar hasta abrir el turno. La proforma si se puede emitir.
            </span>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/caja')}>
            <Icon.caja width={14} height={14} /> Ir a caja
          </button>
        </div>
      )}
      {/* Catalogo */}
      <div>
        <div className="card" style={{ padding: 14, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}><Icon.search width={18} height={18} /></span>
            <input className="input" style={{ paddingLeft: 38 }} placeholder="Buscar producto o SKU..."
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </div>
        </div>

        <div className="flex gap-sm" style={{ flexWrap: 'wrap', marginBottom: 14 }}>
          <button className={`btn btn-sm ${!catActiva ? 'btn-primary' : 'btn-outline'}`} onClick={() => setCatActiva(null)}>Todas</button>
          {categorias.map((c) => (
            <button key={c.id} className={`btn btn-sm ${catActiva === c.id ? 'btn-primary' : 'btn-outline'}`} onClick={() => setCatActiva(c.id)}>{c.nombre}</button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
          {filtrados.map((p) => (
            <button key={p.id} onClick={() => agregar(p)} className="card fade-up"
              style={{ padding: 14, textAlign: 'left', cursor: 'pointer', border: '1px solid var(--glass-border)', background: 'var(--surface)', opacity: p.stock <= 0 ? 0.5 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.categoria_color || 'var(--accent-vivo)' }} />
                <span className="badge" style={{ background: p.stock <= p.stock_minimo ? 'var(--warning-bg)' : 'var(--accent-soft)', color: p.stock <= p.stock_minimo ? 'var(--warning)' : 'var(--accent-texto)', fontSize: 11 }}>{Number(p.stock)}</span>
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3, marginBottom: 6, minHeight: 36 }}>{p.nombre}</div>
              <div style={{ fontWeight: 700, color: 'var(--accent-texto)', fontFamily: 'var(--font-display)' }}>{crc(p.precio_venta)}</div>
            </button>
          ))}
          {filtrados.length === 0 && <p className="muted" style={{ gridColumn: '1/-1', padding: 30, textAlign: 'center' }}>No se encontraron productos</p>}
        </div>
      </div>

      {/* Carrito */}
      <div id="pos-carrito" className="card pos-carrito">
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center gap-sm"><Icon.cart /><h3 style={{ fontSize: 17 }}>Venta actual</h3></div>
        </div>

        {/* minHeight: con el pie alto la lista se encogia hasta una franja
            y no se veia lo que se estaba vendiendo. */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 14, minHeight: 120 }}>
          {carrito.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: 30 }}>Toca un producto para agregarlo</p>
          ) : carrito.map((i) => {
            const bruto = i.cantidad * i.precio_venta;
            const desc = Math.min(bruto, Math.max(0, Number(i.descuento) || 0));
            const lineaNeto = bruto - desc;
            return (
              <div key={i.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{i.nombre}</div>
                    <div className="muted" style={{ fontSize: 13 }}>
                      {crc(i.precio_venta)} c/u
                      {desc > 0 && (
                        <span style={{ color: 'var(--danger)', marginLeft: 6 }}>
                          −{crc(desc)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-sm">
                    <button className="btn btn-ghost btn-sm" style={{ padding: '4px 9px' }} onClick={() => cambiarCantidad(i.id, -1)}>−</button>
                    <span style={{ minWidth: 22, textAlign: 'center', fontWeight: 600 }}>{i.cantidad}</span>
                    <button className="btn btn-ghost btn-sm" style={{ padding: '4px 9px' }} onClick={() => cambiarCantidad(i.id, 1)}>+</button>
                  </div>
                  <div className="mono" style={{ fontWeight: 600, fontSize: 13, minWidth: 64, textAlign: 'right' }}>
                    {crc(lineaNeto)}
                  </div>
                  <button className="btn btn-sm" style={{ padding: 5, color: 'var(--danger)' }} onClick={() => quitar(i.id)}>
                    <Icon.trash width={15} height={15} />
                  </button>
                </div>
                {/* Descuento por linea */}
                <div
                  className="grid-2" style={{ gap: 6, marginTop: 8,
                    paddingLeft: 2,
                  }}
                >
                  <div>
                    <label className="muted" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
                      Desc. %
                    </label>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      placeholder="0"
                      value={i.descuento_pct ?? ''}
                      onChange={(e) => setDescuentoPct(i.id, e.target.value)}
                      style={{ padding: '6px 8px', fontSize: 13 }}
                    />
                  </div>
                  <div>
                    <label className="muted" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
                      Desc. ₡
                    </label>
                    <input
                      className="input mono"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      value={i.descuento || ''}
                      onChange={(e) => setDescuentoMonto(i.id, e.target.value)}
                      style={{ padding: '6px 8px', fontSize: 13 }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: 16, borderTop: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          <div className="flex justify-between" style={{ fontSize: 14, marginBottom: 4 }}>
            <span className="muted">Subtotal</span>
            <span className="mono">{crc(totales.subtotal)}</span>
          </div>
          {(totales.descuentoLineas > 0 || totales.descuentoGlobal > 0) && (
            <div className="flex justify-between" style={{ fontSize: 14, marginBottom: 4, color: 'var(--danger)' }}>
              <span>Descuentos</span>
              <span className="mono">−{crc(totales.descuento)}</span>
            </div>
          )}
          {desglosaIva ? (
            <div className="flex justify-between" style={{ fontSize: 14, marginBottom: 10 }}>
              <span className="muted">IVA</span>
              <span className="mono">{crc(totales.impuesto)}</span>
            </div>
          ) : (
            <p className="muted" style={{ fontSize: 11, margin: '0 0 10px', fontStyle: 'italic' }}>
              {LEYENDA_IVA_INCLUIDO}
            </p>
          )}

          {/* Descuento global de la venta */}
          {carrito.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label className="muted" style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                Descuento global
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{
                      borderRadius: 0,
                      border: 0,
                      background: descGlobalModo === 'pct' ? 'var(--accent)' : 'transparent',
                      color: descGlobalModo === 'pct' ? '#fff' : 'var(--text)',
                      padding: '0 10px',
                    }}
                    onClick={() => setDescGlobalModo('pct')}
                  >
                    %
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{
                      borderRadius: 0,
                      border: 0,
                      background: descGlobalModo === 'monto' ? 'var(--accent)' : 'transparent',
                      color: descGlobalModo === 'monto' ? '#fff' : 'var(--text)',
                      padding: '0 10px',
                    }}
                    onClick={() => setDescGlobalModo('monto')}
                  >
                    ₡
                  </button>
                </div>
                <input
                  className="input mono"
                  type="number"
                  min="0"
                  max={descGlobalModo === 'pct' ? 100 : undefined}
                  step={descGlobalModo === 'pct' ? 0.5 : 1}
                  placeholder={descGlobalModo === 'pct' ? '0 %' : '0 ₡'}
                  value={descGlobalValor}
                  onChange={(e) => setDescGlobalValor(e.target.value)}
                  style={{ flex: 1 }}
                />
              </div>
              {totales.descuentoGlobal > 0 && (
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Se aplican {crc(totales.descuentoGlobal)} de descuento global
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between" style={{ fontSize: 20, fontWeight: 700, marginBottom: 14 }}>
            <span>Total</span>
            <span className="mono" style={{ color: 'var(--accent-texto)' }}>{crc(totales.total)}</span>
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'stretch' }}>
            <select
              className="select"
              style={{ flex: 1, minWidth: 0 }}
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
            >
              <option value="">Cliente: Contado / sin identificar</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                  {c.identificacion ? ` · ${c.identificacion}` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-outline"
              style={{ flexShrink: 0, padding: '0 12px' }}
              onClick={abrirNuevoCliente}
              title="Agregar cliente nuevo"
            >
              <Icon.plus width={18} height={18} />
            </button>
          </div>
          <div style={{ marginBottom: 8 }}>
            <select className="select" value={tipoComp} onChange={(e) => setTipoComp(e.target.value)}>
              {tiposDisponibles.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {!feActiva && (
              <p className="muted" style={{ fontSize: 11, margin: '6px 0 0' }}>
                Factura electronica desactivada: solo tiquetes normales.
              </p>
            )}
          </div>

          {/* Contado vs credito — cuenta por cobrar */}
          <div style={{ marginBottom: 10 }}>
            <label className="muted" style={{ fontSize: 12, display: 'block', marginBottom: 6, fontWeight: 600 }}>
              Condicion de la venta
            </label>
            <div
              className="grid-2" style={{ gap: 8, }}
            >
              <button
                type="button"
                className={`btn btn-block ${!esCredito ? 'btn-primary' : 'btn-outline'}`}
                style={{ padding: '10px 8px', fontSize: 13 }}
                onClick={() => setCondicionVenta('01')}
              >
                Contado
              </button>
              <button
                type="button"
                className={`btn btn-block ${esCredito ? 'btn-primary' : 'btn-outline'}`}
                style={{ padding: '10px 8px', fontSize: 13 }}
                onClick={() => setCondicionVenta('02')}
              >
                Credito
              </button>
            </div>
          </div>

          {esCredito ? (
            <div
              className="card"
              style={{
                padding: 12,
                marginBottom: 10,
                background: 'var(--accent-soft)',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                Venta a credito
              </div>
              <label className="muted" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Plazo del credito (dias)
              </label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {['15', '30', '45', '60', '90'].map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`btn btn-sm ${plazoCredito === d ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setPlazoCredito(d)}
                  >
                    {d} d
                  </button>
                ))}
              </div>
              <input
                className="input"
                type="number"
                min="1"
                max="365"
                value={plazoCredito}
                onChange={(e) => setPlazoCredito(e.target.value)}
                placeholder="Dias de plazo"
                style={{ marginBottom: 8 }}
              />
              {/* Lo que deja pagado hoy entra al cajon con su metodo real; el
                  resto queda en Por cobrar. Sin placeholder a proposito: que
                  se escriba lo que de verdad se recibio. */}
              <label className="muted" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Abono inicial (lo que deja pagado hoy)
              </label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input
                  className="input" type="number" min="0" step="0.01"
                  value={abonoInicial}
                  onChange={(e) => setAbonoInicial(e.target.value)}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <select
                  className="select" value={metodoAbono}
                  onChange={(e) => setMetodoAbono(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  {METODOS_PAGO.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              {Number(abonoInicial) > 0 && carrito.length > 0 && (
                <div className="flex justify-between" style={{ fontSize: 13, marginBottom: 8 }}>
                  <span className="muted">Queda debiendo</span>
                  <span className="mono" style={{
                    fontWeight: 700,
                    color: Number(abonoInicial) + 0.001 >= totales.total ? 'var(--danger)' : 'var(--text)',
                  }}>
                    {Number(abonoInicial) + 0.001 >= totales.total
                      ? 'Paga el total: es contado'
                      : crc(totales.total - Number(abonoInicial))}
                  </span>
                </div>
              )}
              <p className="muted" style={{ fontSize: 12, margin: 0, lineHeight: 1.4 }}>
                El cliente queda con saldo pendiente. Aparece en <strong>Por cobrar</strong> hasta marcar pagado.
              </p>
              {!clienteId && (
                <p style={{ fontSize: 12, margin: '8px 0 0', color: 'var(--danger)' }}>
                  Elegí un cliente (obligatorio en credito).
                </p>
              )}
            </div>
          ) : (
            <select
              className="select"
              style={{ marginBottom: 8 }}
              value={metodoPago}
              onChange={(e) => setMetodoPago(e.target.value)}
            >
              {METODOS_PAGO.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          )}
          {requiereCliente && !esCredito && (
            <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Este tipo exige cliente identificado (cedula / ID).
            </p>
          )}
          {tipoComp === 'proforma' && (
            <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Proforma: no va a Hacienda y no descuenta inventario.
              {esCredito ? ' No se puede emitir proforma a credito.' : ''}
            </p>
          )}
          {!esCredito && metodoPago === 'efectivo' && carrito.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <input className="input" type="number" placeholder="Efectivo recibido"
                value={efectivoRecibido} onChange={(e) => setEfectivoRecibido(e.target.value)} />
              {efectivoRecibido && cambio >= 0 && (
                <div className="flex justify-between" style={{ marginTop: 6, fontSize: 14 }}>
                  <span className="muted">Cambio</span><span className="mono" style={{ fontWeight: 600 }}>{crc(cambio)}</span>
                </div>
              )}
              {efectivoRecibido && cambio < 0 && (
                <div className="flex justify-between" style={{ marginTop: 6, fontSize: 14, color: 'var(--danger)' }}>
                  <span style={{ fontWeight: 600 }}>Faltan</span>
                  <span className="mono" style={{ fontWeight: 700 }}>{crc(-cambio)}</span>
                </div>
              )}
            </div>
          )}
          <button className="btn btn-primary btn-block" disabled={carrito.length === 0 || cobrando || cobraSinCaja} onClick={cobrar} style={{ padding: 14, fontSize: 15 }}>
            {cobrando ? (
              <span className="spinner" />
            ) : esCredito ? (
              // Hoy solo se cobra el abono: el total va en la factura y el
              // resto queda en Por cobrar. Antes decia el total y confundia.
              Number(abonoInicial) > 0
                ? `Registrar credito · cobrar ${crc(Number(abonoInicial))} hoy`
                : `Registrar credito · ${crc(totales.total)} por cobrar`
            ) : (
              `Cobrar ${crc(totales.total)}`
            )}
          </button>
        </div>
      </div>

      {/* En telefono el carrito queda abajo del catalogo: esta barra evita
          tener que rodar toda la pantalla para ver cuanto lleva la venta. */}
      {carrito.length > 0 && (
        <button
          type="button"
          className="pos-fab"
          onClick={() => document.getElementById('pos-carrito')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <span>
            <Icon.cart width={17} height={17} />
            {' '}{carrito.reduce((n, i) => n + i.cantidad, 0)} art.
          </span>
          <span className="mono">{crc(totales.total)}</span>
        </button>
      )}

      {/* Comprobante final */}
      <Modal open={!!ventaFinal} onClose={() => setVentaFinal(null)} title="Comprobante" width={420}>
        {ventaFinal && <ComprobanteView venta={ventaFinal} onClose={() => setVentaFinal(null)} />}
      </Modal>

      {/* Alta rapida de cliente */}
      <Modal
        open={modalCliente}
        onClose={() => !guardandoCliente && setModalCliente(false)}
        title="Nuevo cliente"
        width={440}
      >
        <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          Digite la cedula (fisica 9, juridica 10 o DIMEX 11–12). Se consultan los datos en Hacienda
          automaticamente y el cliente queda seleccionado en esta venta.
        </p>
        <div className="grid-2" style={{ gap: 10 }}>
          <div className="field">
            <label>Tipo ID</label>
            <select
              className="select"
              value={formCliente.tipo_identificacion}
              onChange={(e) =>
                setFormCliente({ ...formCliente, tipo_identificacion: e.target.value })
              }
            >
              {TIPOS_ID.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>
              Cedula / ID
              {requiereCliente ? ' *' : ''}
            </label>
            <input
              className="input mono"
              autoFocus
              value={formCliente.identificacion}
              onChange={(e) => {
                setNombreManual(false);
                setFormCliente({ ...formCliente, identificacion: e.target.value });
              }}
              placeholder="9 a 12 digitos"
            />
          </div>
        </div>

        {/* Estado de la consulta Hacienda */}
        <div style={{ minHeight: 22, marginBottom: 8, fontSize: 12 }}>
          {lookup.cargando && (
            <span className="muted">Consultando Hacienda…</span>
          )}
          {lookup.estado === 'ok' && lookup.datos && (
            <span style={{ color: 'var(--success, #16a34a)', fontWeight: 600 }}>
              Datos de Hacienda: {lookup.datos.situacion?.estado || 'OK'}
              {lookup.datos.regimen ? ` · ${lookup.datos.regimen}` : ''}
              {lookup.datos.actividad_descripcion
                ? ` · ${lookup.datos.actividad_descripcion}`
                : ''}
            </span>
          )}
          {lookup.estado === 'not_found' && (
            <span style={{ color: 'var(--warning, #d97706)' }}>
              No aparece en Hacienda. Puede completar el nombre a mano.
            </span>
          )}
          {lookup.estado === 'error' && (
            <span style={{ color: 'var(--danger)' }}>{lookup.error}</span>
          )}
        </div>

        <div className="field">
          <label>Nombre *</label>
          <input
            className="input"
            value={formCliente.nombre}
            onChange={(e) => {
              setNombreManual(true);
              setFormCliente({ ...formCliente, nombre: e.target.value });
            }}
            placeholder="Se completa solo al consultar la cedula"
          />
        </div>
        <div className="grid-2" style={{ gap: 10 }}>
          <div className="field">
            <label>Telefono</label>
            <input
              className="input"
              value={formCliente.telefono}
              onChange={(e) => setFormCliente({ ...formCliente, telefono: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Correo</label>
            <input
              className="input"
              type="email"
              value={formCliente.email}
              onChange={(e) => setFormCliente({ ...formCliente, email: e.target.value })}
            />
          </div>
        </div>
        <div className="field">
          <label>Ubicacion / direccion</label>
          <input
            className="input"
            value={formCliente.direccion}
            onChange={(e) => setFormCliente({ ...formCliente, direccion: e.target.value })}
            placeholder="Provincia, canton, distrito, senas"
          />
        </div>
        {formCliente.codigo_actividad && (
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Actividad economica: {formCliente.codigo_actividad}
            {lookup.datos?.actividad_descripcion
              ? ` — ${lookup.datos.actividad_descripcion}`
              : ''}
          </p>
        )}
        {requiereCliente && (
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Para factura electronica o exportacion la cedula es obligatoria.
          </p>
        )}
        <div className="flex gap-sm" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
          <button
            type="button"
            className="btn btn-outline"
            disabled={guardandoCliente}
            onClick={() => setModalCliente(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={guardandoCliente}
            onClick={guardarCliente}
          >
            {guardandoCliente ? <span className="spinner" /> : 'Guardar y usar'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
